import { Dispatch, ForwardedRef, forwardRef, ReactElement, SetStateAction, useImperativeHandle, useRef } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bin,
  EmptyStateButton,
  Input,
  IOption,
  Typography,
} from '@weareopenr/component-library';
import { IMenuItem } from 'src/types/menu.types';
import { IFormOptionGroup } from 'src/types/menuItemForm.types';
import styles from './GlobalItemBundleCategoryFields.module.scss';
import { BundleItemFields } from '../BundleItemFields/BundleItemFields';

export interface IMenuItemOptionGroup {
  formFields: UseFormReturn<IFormOptionGroup>;
  itemOptions: IMenuItem[];
  innerRef?: (node?: Element | null | undefined) => void;
  dataScrollRef: (node?: Element | null | undefined) => void;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

export const Listbox = forwardRef(function ListBoxBase(
  props: React.HTMLAttributes<HTMLUListElement>,
  ref: ForwardedRef<HTMLUListElement>,
) {
  const { children, ...rest } = props;
  const innerRef = useRef<HTMLUListElement>(null);

  useImperativeHandle<HTMLUListElement | null, HTMLUListElement | null>(ref, () => innerRef.current);

  return (
    <ul {...rest} ref={innerRef} role="list-box" onClick={(e) => e.preventDefault()}>
      {children}
    </ul>
  );
});

const genericError = 'Please enter a value between 1 and 99';

export const GlobalItemBundleCategoryFields = ({
  formFields,
  itemOptions,
  dataScrollRef,
  setSearchTerm,
}: IMenuItemOptionGroup): ReactElement => {
  const {
    control,
    register,
    formState: { errors },
  } = formFields;

  const { fields, move, append, remove } = useFieldArray({
    control,
    name: `modifiers`,
  });

  const addNewModifier = (): void => {
    append({ optionItemId: '', parentItemId: '', name: '', posId: '', inheritPosId: true });
  };

  const handleDeleteModifier = (index: number) => remove(index);

  const ellipsisGroup = (index: number): IOption[] => {
    return [
      {
        id: 0,
        name: 'Move up',
        isDisabled: index === 0,
        onOptionClick: () => move(index, index - 1),
        persistMenuOnClick: true,
        icon: ArrowUpIcon,
      },
      {
        id: 1,
        name: 'Move down',
        isDisabled: index + 1 === fields.length,
        onOptionClick: () => move(index, index + 1),
        persistMenuOnClick: true,
        icon: ArrowDownIcon,
      },
      { id: 'divider', isDivider: true },
      {
        id: 2,
        name: 'Delete',
        hoverColour: 'tomato',
        onOptionClick: () => handleDeleteModifier(index),
        icon: Bin,
      },
    ];
  };

  return (
    <>
      <div className={styles['form-container']}>
        <Input
          type="text"
          label="Name"
          hasMargin
          register={register('name', {
            required: { value: true, message: 'Please enter a category name' },
          })}
          error={errors?.name?.message}
        />
        <Input type="text" label="Consumer Name" hasMargin register={register('consumer_name')} />
        <Typography weight="semi-bold" className={styles['range']}>
          Range of choices
        </Typography>
        <div className={styles['min-max-container']}>
          <Input
            type="number"
            label="Min"
            register={register('min_choices', {
              valueAsNumber: true,
              required: { value: true, message: genericError },
              validate: {
                isInRange: (value) => (+value >= 0 && +value <= 99) || genericError,
                isUnderMaxValue: (value, formValues) =>
                  +value <= +formValues?.max_choices || "Min can't be greater than max",
              },
              deps: ['max_choices'],
            })}
            error={errors?.min_choices?.message}
          />
          <Input
            type="number"
            label="Max"
            register={register('max_choices', {
              valueAsNumber: true,
              required: {
                value: true,
                message: genericError,
              },
              validate: {
                isInRange: (value) => (+value >= 0 && +value <= 99) || genericError,
                isAboveMin: (value, formValues) => +value >= +formValues?.min_choices || "Max can't be less than min",
                isAboveSelectedItems: (value, formValues) =>
                  +value <= formValues.modifiers?.length || "Max can't be greater than options selected",
              },
              deps: ['min_choices'],
            })}
            error={errors?.max_choices?.message}
          />
        </div>
        {fields.length > 0 && (
          <div className={styles['field-group-headings']} data-testid="field-group-headings">
            <Typography weight="semi-bold">Product Name</Typography>
            <Typography weight="semi-bold" className={styles['centered-heading']}>
              Inherit POS ID
            </Typography>
            <Typography weight="semi-bold">Override POS ID</Typography>
            <Typography weight="semi-bold">Price</Typography>
            <Typography weight="semi-bold">Default Price</Typography>
          </div>
        )}

        {fields?.map((fieldItem, index) => {
          return (
            <BundleItemFields
              dataScrollRef={dataScrollRef}
              formFields={formFields}
              ellipsisMenuOptions={ellipsisGroup(index)}
              itemOptions={itemOptions}
              index={index}
              key={fieldItem.id}
              setSearchTerm={setSearchTerm}
            />
          );
        })}

        <EmptyStateButton
          label="+ Add product"
          onClick={() => {
            addNewModifier();
          }}
        />
      </div>
    </>
  );
};
